import React, { useState, useEffect } from 'react';
import apiServices from '../services/apiService';

/* ─────────────────────────────────────────────
   Embedded styles — Light theme
───────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  .lh-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    padding: 32px;
    box-sizing: border-box;
  }

  /* ── Header ── */
  .lh-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 28px;
    flex-wrap: wrap;
    gap: 16px;
  }
  .lh-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(102,126,234,0.1);
    border: 1px solid rgba(102,126,234,0.25);
    color: #667eea;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    padding: 4px 12px;
    border-radius: 20px;
    margin-bottom: 10px;
  }
  .lh-badge-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #667eea;
    animation: lh-pulse 2s ease-in-out infinite;
  }
  @keyframes lh-pulse {
    0%,100% { opacity:1; transform:scale(1); }
    50%      { opacity:0.5; transform:scale(0.8); }
  }
  .lh-title {
    font-size: 30px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 6px;
    line-height: 1.1;
    letter-spacing: -0.5px;
  }
  .lh-subtitle {
    font-size: 14px;
    color: #94a3b8;
    margin: 0;
    font-weight: 400;
  }

  /* ── Stats ── */
  .lh-stats {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }
  .lh-stat {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 16px 22px;
    min-width: 110px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }
  .lh-stat-value {
    font-size: 26px;
    font-weight: 700;
    color: #1e293b;
    line-height: 1;
  }
  .lh-stat-value.success { color: #10b981; }
  .lh-stat-value.failed  { color: #ef4444; }
  .lh-stat-label {
    font-size: 11px;
    color: #94a3b8;
    margin-top: 4px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    font-weight: 500;
  }

  /* ── Filter card ── */
  .lh-filters {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 24px;
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    position: relative;
    overflow: hidden;
  }
  .lh-filters::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #667eea, #764ba2);
  }
  .lh-filter-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-width: 180px;
  }
  .lh-filter-group label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }
  .lh-input {
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 14px;
    color: #1e293b;
    font-family: 'Inter', sans-serif;
    transition: border-color 0.2s, box-shadow 0.2s;
    outline: none;
    height: 44px;
    box-sizing: border-box;
    width: 100%;
  }
  .lh-input:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102,126,234,0.12);
    background: #fff;
  }
  .lh-input.error {
    border-color: #ef4444;
    box-shadow: 0 0 0 3px rgba(239,68,68,0.1);
  }
  .lh-error-msg {
    font-size: 11px;
    color: #ef4444;
    margin-top: 2px;
    font-weight: 500;
  }

  .lh-filter-actions {
    display: flex;
    gap: 8px;
    align-items: flex-end;
    padding-top: 24px;
  }
  .lh-btn {
    height: 44px;
    padding: 0 20px;
    border-radius: 10px;
    border: none;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 7px;
    transition: all 0.2s;
    font-family: 'Inter', sans-serif;
    white-space: nowrap;
  }
  .lh-btn-primary {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: #fff;
    box-shadow: 0 4px 14px rgba(102,126,234,0.3);
  }
  .lh-btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(102,126,234,0.45);
  }
  .lh-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
  .lh-btn-ghost {
    background: #f1f5f9;
    border: 1.5px solid #e2e8f0;
    color: #64748b;
  }
  .lh-btn-ghost:hover { background: #e2e8f0; color: #475569; }
  .lh-btn-excel {
    background: #f0fdf4;
    border: 1.5px solid #bbf7d0;
    color: #16a34a;
    font-weight: 600;
  }
  .lh-btn-excel:hover:not(:disabled) {
    background: #dcfce7;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(22,163,74,0.15);
  }
  .lh-btn-excel:disabled { opacity: 0.4; cursor: not-allowed; }

  /* ── Table wrapper ── */
  .lh-table-wrap {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }
  .lh-table-header {
    padding: 18px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #f1f5f9;
    flex-wrap: wrap;
    gap: 12px;
    background: #fafbff;
  }
  .lh-table-title {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .lh-count-chip {
    background: rgba(102,126,234,0.1);
    color: #667eea;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 10px;
    border-radius: 20px;
    border: 1px solid rgba(102,126,234,0.2);
  }
  .lh-scroll { overflow-x: auto; }

  table.lh-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13.5px;
  }
  .lh-table thead tr {
    background: #f8fafc;
    border-bottom: 1.5px solid #e2e8f0;
  }
  .lh-table th {
    padding: 12px 16px;
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    letter-spacing: 1px;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .lh-table td {
    padding: 13px 16px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    vertical-align: middle;
    white-space: nowrap;
  }
  .lh-table tbody tr { transition: background 0.15s; }
  .lh-table tbody tr:hover { background: #f8fafc; }
  .lh-table tbody tr:last-child td { border-bottom: none; }

  /* ── Cells ── */
  .lh-cell-emp { display: flex; align-items: center; gap: 10px; }
  .lh-avatar {
    width: 34px; height: 34px;
    border-radius: 8px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700; color: #fff; flex-shrink: 0;
  }
  .lh-emp-code { font-size: 13px; font-weight: 600; color: #667eea; }
  .lh-emp-name { font-size: 13px; font-weight: 500; color: #1e293b; }
  .lh-dept-chip {
    display: inline-block;
    background: #fffbeb; color: #d97706;
    border: 1px solid #fde68a;
    padding: 3px 10px; border-radius: 6px;
    font-size: 12px; font-weight: 500;
    max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .lh-company { font-size: 13px; color: #64748b; }
  .lh-ip {
    font-family: 'Courier New', monospace; font-size: 12px; color: #64748b;
    background: #f1f5f9; padding: 3px 8px; border-radius: 5px; border: 1px solid #e2e8f0;
  }
  .lh-time { display: flex; flex-direction: column; gap: 2px; }
  .lh-date { font-size: 13px; font-weight: 600; color: #1e293b; }
  .lh-clock { font-size: 11px; color: #94a3b8; }
  .lh-status {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600;
  }
  .lh-status.success { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
  .lh-status.failed  { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
  .lh-status-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

  /* ── States ── */
  .lh-state { padding: 80px 20px; text-align: center; }
  .lh-state-icon { font-size: 48px; margin-bottom: 16px; display: block; }
  .lh-state-text { font-size: 15px; font-weight: 500; color: #64748b; margin-bottom: 6px; }
  .lh-state-sub  { font-size: 13px; color: #94a3b8; }
  .lh-spinner {
    width: 36px; height: 36px; border: 3px solid #e2e8f0; border-top-color: #667eea;
    border-radius: 50%; animation: lh-spin 0.8s linear infinite; margin: 0 auto 16px;
  }
  @keyframes lh-spin { to { transform: rotate(360deg); } }

  /* ── Pagination ── */
  .lh-pagination {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 24px; border-top: 1px solid #f1f5f9;
    flex-wrap: wrap; gap: 12px; background: #fafbff;
  }
  .lh-page-info { font-size: 13px; color: #94a3b8; }
  .lh-page-btns { display: flex; gap: 6px; }
  .lh-page-btn {
    width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid #e2e8f0;
    background: #fff; color: #64748b; cursor: pointer; display: flex;
    align-items: center; justify-content: center; font-size: 13px; font-weight: 600;
    transition: all 0.15s; font-family: 'Inter', sans-serif;
  }
  .lh-page-btn:hover:not(:disabled) { background: #f1f5f9; border-color: #667eea; color: #667eea; }
  .lh-page-btn.active { background: linear-gradient(135deg,#667eea,#764ba2); border-color: transparent; color: #fff; }
  .lh-page-btn:disabled { opacity:0.3; cursor:not-allowed; }

  @media (max-width: 768px) {
    .lh-page { padding: 16px; }
    .lh-title { font-size: 24px; }
    .lh-filters { flex-direction: column; }
    .lh-filter-group { min-width: unset; width: 100%; }
    .lh-filter-actions { width: 100%; flex-wrap: wrap; padding-top: 0; }
    .lh-btn { flex: 1; justify-content: center; }
  }
`;

const StyleTag = () => {
  useEffect(() => {
    const id = 'lh-styles';
    if (!document.getElementById(id)) {
      const tag = document.createElement('style');
      tag.id = id; tag.textContent = css;
      document.head.appendChild(tag);
    }
    return () => { const t = document.getElementById('lh-styles'); if (t) t.remove(); };
  }, []);
  return null;
};

const PAGE_SIZE = 15;
const todayStr  = () => new Date().toISOString().split('T')[0];

const formatDate = (iso) => {
  if (!iso) return { date: '-', time: '-' };
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }),
    time: d.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit', second:'2-digit' })
  };
};

const initials = (code) => (code ? String(code).slice(0,2).toUpperCase() : '??');

const exportToExcel = (records) => {
  const headers = ['#','Employee Code','Employee Name','Department','Company','IP Address','Login Date','Login Time','Status','Failure Reason'];
  const rows = records.map((r,i) => {
    const {date,time} = formatDate(r.loginTime);
    return [i+1,r.employeeCode||'-',r.employeeName||'-',r.department||'-',r.company||'-',
            r.iPAddress||r.ipAddress||'-',date,time,r.isSuccess?'Success':'Failed',r.failureReason||'-'];
  });
  const esc  = (v) => { const s=String(v??''); return s.includes(',')||s.includes('"')?`"${s.replace(/"/g,'""')}"`:s; };
  const csv  = [headers,...rows].map(r=>r.map(esc).join(',')).join('\r\n');
  const blob = new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8;'});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href=url; a.download=`LoginHistory_${todayStr()}.csv`; a.click();
  URL.revokeObjectURL(url);
};

/* ═══════════════════════════════════════════ */
const LoginHistoryPage = () => {
  const [records,  setRecords]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [startDate,setStart]    = useState(todayStr()); // ✅ default today
  const [endDate,  setEnd]      = useState(todayStr()); // ✅ default today
  const [startErr, setStartErr] = useState('');
  const [endErr,   setEndErr]   = useState('');
  const [page,     setPage]     = useState(1);
  const [fetched,  setFetched]  = useState(false);

  const totalPages   = Math.ceil(records.length / PAGE_SIZE);
  const pageSlice    = records.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);
  const successCount = records.filter(r => r.isSuccess).length;
  const failedCount  = records.filter(r => !r.isSuccess).length;

  /* ── Validation ── */
  const validate = (start, end) => {
    let ok = true;
    setStartErr(''); setEndErr('');
    if (!start)              { setStartErr('From date is required'); ok=false; }
    if (!end)                { setEndErr('To date is required');     ok=false; }
    if (start && end) {
      if (start > todayStr()) { setStartErr('From date cannot be in the future'); ok=false; }
      if (end   > todayStr()) { setEndErr('To date cannot be in the future');     ok=false; }
      if (start > end)        { setEndErr('To date must be on or after From date'); ok=false; }
      const diff = (new Date(end)-new Date(start))/(1000*60*60*24);
      if (diff > 365)         { setEndErr('Date range cannot exceed 365 days'); ok=false; }
    }
    return ok;
  };

  const fetchHistory = async () => {
    if (!validate(startDate, endDate)) return;
    setLoading(true); setPage(1);
    try {
      const result = await apiServices.getLoginHistory({ startDate, endDate });
      setRecords(result.success ? (result.data||[]) : []);
    } catch { setRecords([]); }
    finally { setLoading(false); setFetched(true); }
  };

  const handleClear = () => {
    setStart(todayStr()); setEnd(todayStr());
    setStartErr(''); setEndErr('');
    setRecords([]); setFetched(false); setPage(1);
  };

  const handleStartChange = (val) => {
    setStart(val); setStartErr('');
    if (endDate && val > endDate) setEndErr('To date must be on or after From date');
    else setEndErr('');
  };

  const handleEndChange = (val) => {
    setEnd(val); setEndErr('');
    if (startDate && val < startDate) setEndErr('To date must be on or after From date');
  };

  useEffect(() => { fetchHistory(); }, []); // eslint-disable-line

  const pageButtons = () => {
    const btns = [];
    for (let i=1;i<=totalPages;i++) {
      if (i===1||i===totalPages||(i>=page-2&&i<=page+2)) btns.push(i);
      else if (btns[btns.length-1]!=='...') btns.push('...');
    }
    return btns;
  };

  return (
    <>
      <StyleTag />
      <div className="lh-page">

        {/* Header */}
        <div className="lh-header">
          <div>
            <div className="lh-badge"><span className="lh-badge-dot"/>Admin Panel</div>
            <h1 className="lh-title">Login History</h1>
            <p className="lh-subtitle">Monitor all access activity across all plants</p>
          </div>
          <div className="lh-stats">
            <div className="lh-stat">
              <div className="lh-stat-value">{records.length}</div>
              <div className="lh-stat-label">Total</div>
            </div>
            <div className="lh-stat">
              <div className="lh-stat-value success">{successCount}</div>
              <div className="lh-stat-label">Success</div>
            </div>
            <div className="lh-stat">
              <div className="lh-stat-value failed">{failedCount}</div>
              <div className="lh-stat-label">Failed</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="lh-filters">
          <div className="lh-filter-group">
            <label>From Date <span style={{color:'#ef4444'}}>*</span></label>
            <input type="date" className={`lh-input ${startErr?'error':''}`}
              value={startDate} max={todayStr()}
              onChange={e=>handleStartChange(e.target.value)} />
            {startErr && <div className="lh-error-msg">⚠ {startErr}</div>}
          </div>
          <div className="lh-filter-group">
            <label>To Date <span style={{color:'#ef4444'}}>*</span></label>
            <input type="date" className={`lh-input ${endErr?'error':''}`}
              value={endDate} min={startDate} max={todayStr()}
              onChange={e=>handleEndChange(e.target.value)} />
            {endErr && <div className="lh-error-msg">⚠ {endErr}</div>}
          </div>
          <div className="lh-filter-actions">
            <button className="lh-btn lh-btn-primary" onClick={fetchHistory} disabled={loading}>
              {loading
                ? <><span style={{width:14,height:14,border:'2px solid rgba(255,255,255,0.4)',borderTopColor:'#fff',borderRadius:'50%',display:'inline-block',animation:'lh-spin 0.8s linear infinite'}}/>Searching…</>
                : <><span>🔍</span>Search</>}
            </button>
            <button className="lh-btn lh-btn-ghost" onClick={handleClear} disabled={loading}>✕ Clear</button>
            <button className="lh-btn lh-btn-excel"
              onClick={()=>exportToExcel(records)}
              disabled={records.length===0||loading}
              title="Download as CSV (opens in Excel)">
              ⬇ Export Excel
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="lh-table-wrap">
          <div className="lh-table-header">
            <div className="lh-table-title">
              Activity Log <span className="lh-count-chip">{records.length} records</span>
            </div>
            {startDate && endDate && fetched && (
              <div style={{fontSize:12,color:'#94a3b8'}}>{startDate} → {endDate}</div>
            )}
          </div>

          <div className="lh-scroll">
            {loading ? (
              <div className="lh-state">
                <div className="lh-spinner"/>
                <div className="lh-state-text">Fetching login records…</div>
                <div className="lh-state-sub">Please wait</div>
              </div>
            ) : !fetched ? (
              <div className="lh-state">
                <span className="lh-state-icon">📋</span>
                <div className="lh-state-text">Select a date range and click Search</div>
              </div>
            ) : records.length===0 ? (
              <div className="lh-state">
                <span className="lh-state-icon">🔎</span>
                <div className="lh-state-text">No records found for selected range</div>
                <div className="lh-state-sub">Try a different date range</div>
              </div>
            ) : (
              <table className="lh-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Employee Code</th>
                    <th>Employee Name</th>
                    <th>Department</th>
                    <th>Company</th>
                    <th>IP Address</th>
                    <th>Login Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pageSlice.map((r,i) => {
                    const {date,time} = formatDate(r.loginTime);
                    return (
                      <tr key={r.id||i}>
                        <td style={{color:'#94a3b8',fontSize:12}}>{(page-1)*PAGE_SIZE+i+1}</td>
                        <td>
                          <div className="lh-cell-emp">
                            <div className="lh-avatar">{initials(r.employeeCode)}</div>
                            <span className="lh-emp-code">{r.employeeCode||'-'}</span>
                          </div>
                        </td>
                        <td><span className="lh-emp-name">{r.employeeName||'-'}</span></td>
                        <td>
                          <span className="lh-dept-chip" title={r.department}>
                            {r.department&&r.department!=='-'?r.department:'—'}
                          </span>
                        </td>
                        <td><span className="lh-company">{r.company||'-'}</span></td>
                        <td><span className="lh-ip">{r.iPAddress||r.ipAddress||'—'}</span></td>
                        <td>
                          <div className="lh-time">
                            <span className="lh-date">{date}</span>
                            <span className="lh-clock">{time}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`lh-status ${r.isSuccess?'success':'failed'}`}>
                            <span className="lh-status-dot"/>
                            {r.isSuccess?'Success':'Failed'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {totalPages>1 && (
            <div className="lh-pagination">
              <div className="lh-page-info">
                Showing {(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE,records.length)} of {records.length}
              </div>
              <div className="lh-page-btns">
                <button className="lh-page-btn" onClick={()=>setPage(p=>p-1)} disabled={page===1}>‹</button>
                {pageButtons().map((b,i) =>
                  b==='...'
                    ? <span key={i} style={{color:'#94a3b8',padding:'0 4px',lineHeight:'34px'}}>…</span>
                    : <button key={b} className={`lh-page-btn ${b===page?'active':''}`} onClick={()=>setPage(b)}>{b}</button>
                )}
                <button className="lh-page-btn" onClick={()=>setPage(p=>p+1)} disabled={page===totalPages}>›</button>
              </div>
            </div>
          )}
        </div>

      </div>
    </>
  );
};

export default LoginHistoryPage;